// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { auth } from '../firebaseAuth';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { runPreLogoutCleanups } from '../utils/preLogoutCleanup';
import { getXpLevel } from '../utils/xpLevels';

const AuthContext = createContext();
let firestoreDepsPromise;


function loadFirestoreDeps() {
  if (!firestoreDepsPromise) {
    firestoreDepsPromise = Promise.all([
      import('../firebase'),
      import('firebase/firestore'),
    ]).then(([{ db }, firestore]) => ({ db, ...firestore }));
  }

  return firestoreDepsPromise;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [levelUp, setLevelUp] = useState(null);
  const [dailyLoginEvent, setDailyLoginEvent] = useState(null);
  const prevLevelRef = useRef(null);
  const xpUnsubRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async user => {
      if (user) {
        // Security gate: block unverified email/password accounts (RUN FIRST, before any Firestore ops)
        const isEmailProvider = user.providerData.some(p => p.providerId === 'password');
        if (isEmailProvider && !user.emailVerified) {
          // Force reload to get the latest emailVerified status from the server
          try { await user.reload(); } catch {}
          if (!user.emailVerified) {
            await signOut(auth);
            setCurrentUser(null);
            setLoading(false);
            return;
          }
        }

        try {
          const { callTrusted } = await import('../utils/trustedApi');
          const response = await callTrusted('ensureAccount');
          if (response.dailyLoginEvent) setDailyLoginEvent(response.dailyLoginEvent);
        } catch (err) {
          console.error('Error initializing user profile:', err);
          // swallow so auth still proceeds
        }

        setCurrentUser(user);

        // Start XP watcher for level-up detection
        if (xpUnsubRef.current) xpUnsubRef.current();
        prevLevelRef.current = null; // reset on (re)login
        try {
          const { db, doc, onSnapshot } = await loadFirestoreDeps();
          xpUnsubRef.current = onSnapshot(doc(db, 'users', user.uid), snap => {
            if (!snap.exists()) return;
            const userData = snap.data();
            const xp = userData.xp || 0;
            const newLevel = getXpLevel(xp);
            const prev = prevLevelRef.current;
            if (prev !== null && newLevel.level > prev.level) {
              setLevelUp({ prevLevel: prev, newLevel, currentXp: xp });
            }
            import('../utils/notifications')
              .then(({ syncLevelUpNotification }) => syncLevelUpNotification({ userUid: user.uid, userData, currentLevel: newLevel.level }))
              .catch(() => {});
            prevLevelRef.current = newLevel;
          });
        } catch (err) {
          console.error('Error starting XP watcher:', err);
        }
      } else {
        if (xpUnsubRef.current) { xpUnsubRef.current(); xpUnsubRef.current = null; }
        prevLevelRef.current = null;
        setCurrentUser(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const logout = useCallback(async () => {
    await runPreLogoutCleanups();
    await signOut(auth);
  }, []);

  const clearLevelUp = useCallback(() => setLevelUp(null), []);
  const clearDailyLogin = useCallback(() => setDailyLoginEvent(null), []);

  return (
    <AuthContext.Provider value={{ currentUser, authLoading: loading, levelUp, clearLevelUp, dailyLoginEvent, clearDailyLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
